interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  variant?: "error" | "warning" | "info" | "success";
  actionLabel?: string;
  onAction?: () => void;
}

export function ErrorModal({
  isOpen,
  onClose,
  title,
  message,
  variant = "error",
  actionLabel = "Okay",
  onAction,
}: ErrorModalProps) {
  if (!isOpen) return null;

  const handleAction = () => {
    if (onAction) {
      onAction();
    } else {
      onClose();
    }
  };

  // Variant-specific styling
  const variantConfig = {
    error: {
      iconBg: "bg-red-500/20",
      iconColor: "text-red-500",
      icon: "error",
      defaultTitle: "Error",
    },
    warning: {
      iconBg: "bg-orange-500/20",
      iconColor: "text-orange-500",
      icon: "warning",
      defaultTitle: "Warning",
    },
    info: {
      iconBg: "bg-blue-500/20",
      iconColor: "text-blue-500",
      icon: "info",
      defaultTitle: "Information",
    },
    success: {
      iconBg: "bg-green-500/20",
      iconColor: "text-green-500",
      icon: "check_circle",
      defaultTitle: "Success",
    },
  };

  const config = variantConfig[variant];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative flex w-full max-w-sm flex-col bg-card-light dark:bg-gray-900 rounded-3xl p-6 shadow-xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <span className="material-symbols-outlined text-gray-600 dark:text-gray-400">
            close
          </span>
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div
            className={`flex items-center justify-center w-16 h-16 rounded-full ${config.iconBg}`}
          >
            <span
              className={`material-symbols-outlined ${config.iconColor} text-4xl`}
            >
              {config.icon}
            </span>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-gray-900 dark:text-white text-xl font-bold text-center mb-2">
          {title || config.defaultTitle}
        </h2>

        {/* Description */}
        <p className="text-gray-600 dark:text-gray-400 text-sm text-center mb-6">
          {message || "Something went wrong. Please try again."}
        </p>

        {/* Action Button */}
        <button
          onClick={handleAction}
          className="w-full h-12 rounded-xl bg-primary text-black font-bold hover:opacity-90 transition-opacity"
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
}
